// Panel multi-tab safety: stamps every write form in the panel layout with a
// hidden _panel_entity_id input sourced from the panel-entity-id <meta> tag.
// The server-side guard in Panel::PanelController#guard_against_stale_panel_context
// compares this against the current @entity and rejects the submission if it
// doesn't match — catching multi-tab races where a sysadmin switches entities
// in one tab and submits an open form in another.
//
// Only the entity is stamped: a write's site is no longer session-derived (it
// travels in the request's URL/record/payload), so there is no site drift to
// guard against. The panel-site-id <meta> tag, if present, is simply unread.
//
// On non-panel pages the meta tag is absent and every code path here is a
// no-op, so it's safe to load globally.

const ENTITY_META_NAME = "panel-entity-id";
const ENTITY_FIELD_NAME = "_panel_entity_id";

function metaContent(name) {
  const el = document.querySelector(`meta[name="${name}"]`);
  const content = el?.content;
  return content && content.length > 0 ? content : null;
}

function currentStamps() {
  return {
    entityId: metaContent(ENTITY_META_NAME),
  };
}

function ensureHidden(form, name, value) {
  if (!value) return;
  let input = form.querySelector(`input[type="hidden"][name="${name}"]`);
  if (!input) {
    input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

function isWriteForm(form) {
  const method = (form.getAttribute("method") || "GET").toUpperCase();
  if (method === "GET") return false;
  // Rails uses POST with a hidden _method for PATCH/PUT/DELETE; both are stamped.
  return true;
}

export function stampForm(form, stamps = currentStamps()) {
  if (!stamps.entityId) return;
  if (!isWriteForm(form)) return;
  ensureHidden(form, ENTITY_FIELD_NAME, stamps.entityId);
}

export function stampAllForms() {
  const stamps = currentStamps();
  if (!stamps.entityId) return;
  document.querySelectorAll("form").forEach((form) => stampForm(form, stamps));
}

function initObserver() {
  const observer = new MutationObserver((mutations) => {
    const stamps = currentStamps();
    if (!stamps.entityId) return;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.tagName === "FORM") {
          stampForm(node, stamps);
        } else if (typeof node.querySelectorAll === "function") {
          node.querySelectorAll("form").forEach((form) => stampForm(form, stamps));
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return observer;
}

function init() {
  stampAllForms();

  // Re-stamp on every Turbo lifecycle event that might swap content in.
  ["turbo:load", "turbo:render", "turbo:frame-render", "turbo:before-stream-render"].forEach(
    (event) => document.addEventListener(event, stampAllForms)
  );

  // Catch forms inserted outside Turbo's lifecycle (Stimulus, modals, etc.).
  initObserver();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
